import React, { useEffect, useMemo, useState } from 'react';
import { useInventoryStore, cataloguePickerItems } from '../../../store/inventoryStore';
import { useSuppliersStore } from '../../../store/suppliersStore';
import {
  useStockRequestStore,
  StockRequest,
  StockRequestLine,
  isLineApprovable,
  isLineOrderable,
  isLineReceivable,
  isLineIssueable,
  effectiveLineItemId,
} from '../../../store/stockRequestStore';
import { getRequestTheme, actionBadge, actionBadgeClass } from './theme';
import ReceiveStockPanel, { ReceiveTarget } from './ReceiveStockPanel';

export type SheetLineAction = 'approve' | 'ordered' | 'receive' | 'issue';

export const LINE_ACTION_DEFS: { key: SheetLineAction; label: string }[] = [
  { key: 'approve', label: 'Approve' },
  { key: 'ordered', label: 'Ordered' },
  { key: 'receive', label: 'Receive' },
  { key: 'issue', label: 'Receive Issue' },
];

export function lineActionEnabled(action: SheetLineAction, line: StockRequestLine): boolean {
  switch (action) {
    case 'approve': return isLineApprovable(line);
    case 'ordered': return isLineOrderable(line);
    case 'receive': return isLineReceivable(line);
    case 'issue': return isLineIssueable(line);
    default: return false;
  }
}

export function lineActionDisabledReason(action: SheetLineAction, line: StockRequestLine): string {
  if (lineActionEnabled(action, line)) return '';
  switch (action) {
    case 'approve':
      return 'Available when the line is draft or awaiting review';
    case 'ordered':
      return 'Approve this line first';
    case 'receive':
      return 'Mark as ordered first, then receive remaining qty';
    case 'issue':
      return 'Available after the line has been ordered';
    default:
      return '';
  }
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function groupByRequest(targets: ReceiveTarget[]): Array<[string, ReceiveTarget[]]> {
  const map = new Map<string, ReceiveTarget[]>();
  for (const t of targets) {
    const list = map.get(t.request.id) ?? [];
    list.push(t);
    map.set(t.request.id, list);
  }
  return Array.from(map.entries());
}

interface Props {
  targets: ReceiveTarget[];
  isDark: boolean;
  initialAction?: SheetLineAction | null;
  lockAction?: boolean;
  onCancel?: () => void;
  onComplete: (message: string) => void;
}

const LineActionsPanel: React.FC<Props> = ({
  targets,
  isDark,
  initialAction = null,
  lockAction = false,
  onCancel,
  onComplete,
}) => {
  const theme = getRequestTheme(isDark);
  const { items } = useInventoryStore();
  const { suppliers, fetchSuppliers } = useSuppliersStore();
  const { approveSheetLines, markOrdered, reportReceiveIssue } = useStockRequestStore();

  const [openAction, setOpenAction] = useState<SheetLineAction | null>(initialAction);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [approveQty, setApproveQty] = useState<Record<string, number>>({});
  const [orderQty, setOrderQty] = useState<Record<string, number>>({});
  const [orderDate, setOrderDate] = useState(todayIso());
  const [orderSupplier, setOrderSupplier] = useState('');
  const [orderEta, setOrderEta] = useState('');
  const [issueReason, setIssueReason] = useState('');
  const [issueQty, setIssueQty] = useState('');
  const [issueItemId, setIssueItemId] = useState('');

  useEffect(() => {
    void fetchSuppliers();
  }, [fetchSuppliers]);

  useEffect(() => {
    if (initialAction) setOpenAction(initialAction);
  }, [initialAction]);

  const linesKey = targets.map(t => t.line.id).join('|');
  useEffect(() => {
    const nextApprove: Record<string, number> = {};
    const nextOrder: Record<string, number> = {};
    let supplier = '';
    let eta = '';
    for (const { line } of targets) {
      nextApprove[line.id] = line.approvedQty ?? line.requestedQty;
      nextOrder[line.id] = line.orderedQty ?? line.approvedQty ?? line.requestedQty;
      if (!supplier && line.supplier) supplier = line.supplier;
      if (!eta && line.expectedDate) eta = line.expectedDate.slice(0, 10);
    }
    setApproveQty(nextApprove);
    setOrderQty(nextOrder);
    setOrderSupplier(supplier);
    setOrderEta(eta);
    setOrderDate(todayIso());
    setIssueReason('');
    setIssueQty('');
    setIssueItemId('');
    setError('');
  }, [linesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const itemNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of items) map.set(item.id, item.name);
    return map;
  }, [items]);

  const lineLabel = (line: StockRequestLine) => {
    const id = effectiveLineItemId(line);
    if (id && itemNameById.has(id)) return itemNameById.get(id)!;
    return line.freeTextDescription ?? 'Item';
  };

  const anyEnabled = (action: SheetLineAction) => targets.some(t => lineActionEnabled(action, t.line));
  const qualifying = (action: SheetLineAction) => targets.filter(t => lineActionEnabled(action, t.line));

  const inputCls =
    'text-sm px-2 py-1.5 rounded border w-full focus:outline-none focus:ring-1 focus:ring-blue-500 box-border';
  const qtyCls =
    'text-sm text-center w-16 h-7 px-1 py-0 rounded border box-border leading-none focus:outline-none focus:ring-1 focus:ring-blue-500';

  const confirmApprove = async () => {
    const rows = qualifying('approve');
    if (rows.length === 0) return;
    setSaving(true);
    setError('');
    for (const [requestId, group] of groupByRequest(rows)) {
      const ok = await approveSheetLines(
        requestId,
        group.map(({ line }) => ({
          lineId: line.id,
          approvedQty: Math.max(1, approveQty[line.id] ?? line.requestedQty),
        })),
      );
      if (!ok) {
        setSaving(false);
        setError(useStockRequestStore.getState().error ?? 'Approve failed');
        return;
      }
    }
    setSaving(false);
    setOpenAction(null);
    onComplete(rows.length === 1 ? 'Line approved.' : `${rows.length} lines approved.`);
  };

  const confirmOrdered = async () => {
    const rows = qualifying('ordered');
    if (rows.length === 0) return;
    if (!orderSupplier.trim()) {
      setError('Supplier is required.');
      return;
    }
    setSaving(true);
    setError('');
    for (const [requestId, group] of groupByRequest(rows)) {
      const ok = await markOrdered(
        requestId,
        group.map(({ line }) => ({
          lineId: line.id,
          supplier: orderSupplier.trim(),
          expectedDate: orderEta || null,
          orderedQty: Math.max(1, orderQty[line.id] ?? line.approvedQty ?? line.requestedQty),
          orderedAt: orderDate || null,
        })),
      );
      if (!ok) {
        setSaving(false);
        setError(useStockRequestStore.getState().error ?? 'Failed to mark as ordered');
        return;
      }
    }
    setSaving(false);
    setOpenAction(null);
    onComplete(rows.length === 1 ? 'Line marked as ordered.' : `${rows.length} lines marked as ordered.`);
  };

  const confirmIssue = async () => {
    const rows = qualifying('issue');
    if (rows.length === 0) return;
    if (!issueReason.trim()) {
      setError('Describe the issue.');
      return;
    }
    const qty = issueQty.trim() ? Math.max(0, parseInt(issueQty, 10) || 0) : 0;
    setSaving(true);
    setError('');
    let lastMessage = 'Issue flagged.';
    for (const { request, line } of rows) {
      const result = await reportReceiveIssue(request.id, {
        lineId: line.id,
        reason: issueReason.trim(),
        qty: qty > 0 ? qty : undefined,
        itemId: issueItemId || null,
      });
      if (!result.success) {
        setSaving(false);
        setError(result.message);
        return;
      }
      lastMessage = result.message;
    }
    setSaving(false);
    setOpenAction(null);
    onComplete(lastMessage);
  };

  const fieldStyle = {
    backgroundColor: theme.inputBg,
    borderColor: theme.border,
    color: theme.text,
    colorScheme: (isDark ? 'dark' : 'light') as 'dark' | 'light',
  };

  const showNames = targets.length > 1;

  return (
    <div className="space-y-3">
      {!lockAction && (
        <div className="flex flex-wrap gap-1.5">
          {LINE_ACTION_DEFS.map(def => {
            const enabled = anyEnabled(def.key);
            const active = openAction === def.key;
            const reason = !enabled && targets[0]
              ? lineActionDisabledReason(def.key, targets[0].line)
              : undefined;
            return (
              <button
                key={def.key}
                type="button"
                disabled={!enabled}
                title={reason}
                onClick={() => setOpenAction(a => (a === def.key ? null : def.key))}
                className={`${actionBadgeClass} ${actionBadge(def.key, isDark, { active, interactive: enabled })}`}
              >
                {def.label}
              </button>
            );
          })}
        </div>
      )}

      {openAction === 'approve' && (
        <div className="space-y-2">
          <p className="text-xs" style={{ color: theme.muted }}>
            Sets approve qty and moves the line to Awaiting Order.
          </p>
          {qualifying('approve').map(({ line }) => (
            <div key={line.id} className="flex items-center justify-between gap-3">
              <span className="text-sm truncate" style={{ color: theme.text }}>
                {showNames ? lineLabel(line) : 'Approve qty'}
              </span>
              <input
                type="number"
                min={1}
                value={approveQty[line.id] ?? line.requestedQty}
                onChange={e => setApproveQty(prev => ({
                  ...prev,
                  [line.id]: Math.max(1, parseInt(e.target.value, 10) || 1),
                }))}
                className={qtyCls}
                style={fieldStyle}
              />
            </div>
          ))}
          <div className="flex justify-end">
            <button
              type="button"
              disabled={saving}
              onClick={() => void confirmApprove()}
              className="inline-flex items-center h-8 px-3 rounded text-xs font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Confirm approve'}
            </button>
          </div>
        </div>
      )}

      {openAction === 'ordered' && (
        <div className="space-y-2">
          <p className="text-xs" style={{ color: theme.muted }}>
            Records the order. Supplier is required.
          </p>
          {qualifying('ordered').map(({ line }) => (
            <div key={line.id} className="flex items-center justify-between gap-3">
              <span className="text-sm truncate" style={{ color: theme.text }}>
                {showNames ? lineLabel(line) : 'Qty ordered'}
              </span>
              <input
                type="number"
                min={1}
                value={orderQty[line.id] ?? (line.approvedQty ?? line.requestedQty)}
                onChange={e => setOrderQty(prev => ({
                  ...prev,
                  [line.id]: Math.max(1, parseInt(e.target.value, 10) || 1),
                }))}
                className={qtyCls}
                style={fieldStyle}
              />
            </div>
          ))}
          <label className="block text-xs" style={{ color: theme.muted }}>
            Ordered date
            <input type="date" value={orderDate} onChange={e => setOrderDate(e.target.value)} className={`${inputCls} mt-1`} style={fieldStyle} />
          </label>
          <label className="block text-xs" style={{ color: theme.muted }}>
            Supplier
            <select value={orderSupplier} onChange={e => setOrderSupplier(e.target.value)} className={`${inputCls} mt-1`} style={fieldStyle}>
              <option value="">Select…</option>
              {suppliers.map(s => (
                <option key={s.id} value={s.name}>{s.name}</option>
              ))}
            </select>
          </label>
          <label className="block text-xs" style={{ color: theme.muted }}>
            ETA
            <input type="date" value={orderEta} onChange={e => setOrderEta(e.target.value)} className={`${inputCls} mt-1`} style={fieldStyle} />
          </label>
          <div className="flex justify-end">
            <button
              type="button"
              disabled={saving}
              onClick={() => void confirmOrdered()}
              className="inline-flex items-center h-8 px-3 rounded text-xs font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Confirm ordered'}
            </button>
          </div>
        </div>
      )}

      {openAction === 'receive' && (
        <ReceiveStockPanel
          key={targets.map(t => `${t.line.id}:${t.line.receivedQty}`).join('|')}
          targets={qualifying('receive')}
          isDark={isDark}
          showLineNames={showNames}
          onCancel={() => {
            setOpenAction(null);
            onCancel?.();
          }}
          onComplete={message => {
            setOpenAction(null);
            onComplete(message);
          }}
        />
      )}

      {openAction === 'issue' && (
        <div className="space-y-2">
          <p className="text-xs" style={{ color: theme.muted }}>
            Flag a wrong qty or item. Optional qty/item are added to inventory, then the line is set to Disputed.
          </p>
          <label className="block text-xs" style={{ color: theme.muted }}>
            What went wrong
            <input
              type="text"
              value={issueReason}
              onChange={e => setIssueReason(e.target.value)}
              placeholder="Wrong item, short delivery…"
              className={`${inputCls} mt-1`}
              style={fieldStyle}
            />
          </label>
          <label className="block text-xs" style={{ color: theme.muted }}>
            Qty actually received (optional)
            <input
              type="number"
              min={0}
              value={issueQty}
              onChange={e => setIssueQty(e.target.value)}
              className={`${inputCls} mt-1`}
              style={fieldStyle}
            />
          </label>
          <label className="block text-xs" style={{ color: theme.muted }}>
            Item actually received (optional)
            <select
              value={issueItemId}
              onChange={e => setIssueItemId(e.target.value)}
              className={`${inputCls} mt-1`}
              style={fieldStyle}
            >
              <option value="">Same as requested</option>
              {cataloguePickerItems(items).map(i => (
                <option key={i.id} value={i.id}>{i.name}</option>
              ))}
            </select>
          </label>
          <div className="flex justify-end">
            <button
              type="button"
              disabled={saving}
              onClick={() => void confirmIssue()}
              className="inline-flex items-center h-8 px-3 rounded text-xs font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Confirm issue'}
            </button>
          </div>
        </div>
      )}

      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
};

export default LineActionsPanel;
export type { ReceiveTarget };
